import { TriangleAlert } from "lucide-react";
import { useCanEdit } from "@/model/access";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { fmtByDate, fmtMonth, GRID, NOW, sortMonths } from "@/lib/months";
import { cn } from "@/lib/utils";
import { useStore } from "@/model/store";

/** Hero number: 56px on phones, 72px from `sm` up; always the model teal. */
export const HERO_NUMBER = "text-[56px] leading-none font-semibold tracking-tighter text-[var(--model)] tabular-nums sm:text-7xl";

/**
 * The headline date as a visible control in the hero ("by end of 2028 ▾"), listing the key dates.
 * The key-dates table headers remain a second way to set it.
 */
export function HeroDateSelect({
	value,
	dates,
	onChange,
	format = fmtByDate,
}: {
	value: string;
	dates: string[];
	onChange: (m: string) => void;
	format?: (m: string) => string;
}) {
	const options = sortMonths([...new Set([...dates, value])]);
	return (
		<Select value={value} onValueChange={onChange}>
			<SelectTrigger
				aria-label="Headline date"
				className="inline-flex h-auto rounded-md border-0 border-b border-dashed border-foreground/40 bg-transparent px-1 py-0 text-xl text-foreground shadow-none hover:bg-muted dark:bg-transparent data-[size=default]:h-auto [&_svg:not([class*='size-'])]:size-4"
			>
				<SelectValue />
			</SelectTrigger>
			<SelectContent position="popper" align="start">
				{options.map((m) => (
					<SelectItem key={m} value={m}>
						{format(m)}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

/** One number in the stat row. Labels wrap to two lines rather than truncate (A8). */
export function Stat({
	label,
	value,
	tone,
	pending,
}: {
	label: string;
	value: string;
	tone: "baseline" | "model" | "scenario" | "market" | "ink";
	pending?: boolean;
}) {
	return (
		<div className={cn("border-l-2 pl-3", pending && "opacity-60")} style={{ borderColor: `var(--${tone})` }}>
			<div className="line-clamp-2 max-w-40 text-xs leading-snug text-muted-foreground" title={label}>
				{label}
			</div>
			<div className="text-2xl font-semibold tabular-nums" style={{ color: tone === "ink" ? undefined : `var(--${tone})` }}>
				{value}
			</div>
		</div>
	);
}

/** §18.2 soft warning `market_tension`: the baseline is far from a market that has a view. */
export function TensionBadge({ baseline, market, date, venue }: { baseline: string; market: string; date: string; venue: string }) {
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span
					tabIndex={0}
					className="inline-flex items-center gap-1.5 rounded-full border border-[var(--scenario)]/50 bg-[var(--scenario)]/10 px-2.5 py-0.5 text-xs font-medium text-foreground"
				>
					<TriangleAlert className="size-3.5 text-[var(--scenario)]" aria-hidden />
					Far from the market: {baseline} vs {market} by {date}
				</span>
			</TooltipTrigger>
			<TooltipContent className="max-w-72">
				The baseline says {baseline} by {date}; {venue} traders say {market}. That is more than 25 points apart. It may be right, but it is worth a second look,
				or use the market for the baseline from the market panel.
			</TooltipContent>
		</Tooltip>
	);
}

/**
 * Resolution, behind a small "Resolve…" button (C4). Once set, the "Happened … · Reopen" line is shown in the hero
 * instead (see `HappenedLine`).
 */
export function ResolvePanel({ nodeId }: { nodeId: string }) {
	const markHappened = useStore((s) => s.markHappened);
	const [open, setOpen] = useState(false);
	const [month, setMonth] = useState(NOW);
	const canEdit = useCanEdit();
	if (!canEdit) return <p className="text-sm text-muted-foreground">Not resolved yet.</p>;
	if (!open)
		return (
			<div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
				<span>Not resolved yet.</span>
				<Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
					Resolve…
				</Button>
			</div>
		);
	return (
		<div className="flex flex-wrap items-center gap-2 text-sm">
			<label htmlFor={`happened-${nodeId}`} className="text-muted-foreground">
				It happened in
			</label>
			<Input id={`happened-${nodeId}`} type="month" className="h-8 w-40" max={GRID[GRID.length - 1]} value={month} onChange={(e) => setMonth(e.target.value)} />
			<Button
				size="sm"
				variant="outline"
				disabled={!month}
				onClick={() => {
					markHappened(nodeId, month);
					setOpen(false);
				}}
			>
				Mark as happened
			</Button>
			<Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
				Cancel
			</Button>
		</div>
	);
}

export function HappenedLine({ nodeId, month }: { nodeId: string; month: string }) {
	const markHappened = useStore((s) => s.markHappened);
	const canEdit = useCanEdit();
	return (
		<p className="mt-3 flex items-center gap-2 text-sm">
			<span className="font-medium text-[var(--gain)]">Happened {fmtMonth(month)}</span>
			{canEdit && <span className="text-muted-foreground">·</span>}
			<button
				hidden={!canEdit}
				type="button"
				className="text-muted-foreground underline underline-offset-2 hover:text-foreground"
				onClick={() => markHappened(nodeId, null)}
			>
				Reopen
			</button>
		</p>
	);
}

/** Chart preview height: 320px, or 240px on phones (A2). */
export function usePreviewHeight() {
	const q = "(max-width: 639px)";
	const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
	useEffect(() => {
		const mq = window.matchMedia(q);
		const on = () => setNarrow(mq.matches);
		mq.addEventListener("change", on);
		return () => mq.removeEventListener("change", on);
	}, []);
	return narrow ? 240 : 320;
}
